import { type ReactNode } from 'react';
import { useSharedPermissions } from '../contexts/PermissionsContext';
import { type UserPermissions } from '../hooks/useAuth';
import { Card, CardContent } from './ui/card';
import { Shield, AlertTriangle } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  permission?: keyof UserPermissions;
  capability?: string;
  anyCapability?: string[];
  fallback?: ReactNode;
}

export function ProtectedRoute({ children, permission, capability, anyCapability, fallback }: ProtectedRouteProps) {
  const permissions = useSharedPermissions();
  const hasPermission = anyCapability ? anyCapability.some(item => permissions.allows?.(item) === true) : capability ? permissions.allows?.(capability) === true : !!permission && permissions[permission] === true;

  if (!hasPermission) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-destructive" />
            </div>
            <h3 className="text-lg font-medium mb-2">Access Denied</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You don't have permission to access this resource.
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="h-3 w-3" />
              Contact your administrator if you believe this is an error
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
